import { useCallback, useSyncExternalStore } from 'react';

/** The breakpoint tokens `theme.css` defines, and the only names this takes. */
export type Breakpoint = 'mobile' | 'agenda' | 'desk';

/*
 * Read from the token the styling uses rather than written out again here: a
 * second copy of 1600 is free to drift from what `desk:` means in a class.
 */
function atLeastQuery(name: Breakpoint): MediaQueryList {
  const width = getComputedStyle(document.documentElement).getPropertyValue(
    `--breakpoint-${name}`,
  );

  return window.matchMedia(`(width >= ${width})`);
}

/**
 * Whether the window is at least as wide as a breakpoint — the same answer the
 * `desk:` variant gives a class, for the parts of a layout CSS cannot settle.
 *
 * Choosing between two navigations is such a part. Hiding one of them with
 * `display:none` leaves it in the page, and a nav that is in the page is a
 * landmark a screen reader offers — so the width has to decide which one is
 * rendered at all, and it has to be this same width or the two disagree.
 */
export function useAtLeast(name: Breakpoint): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const query = atLeastQuery(name);
      query.addEventListener('change', onChange);

      return () => query.removeEventListener('change', onChange);
    },
    [name],
  );

  return useSyncExternalStore(
    subscribe,
    () => atLeastQuery(name).matches,
    /* Rendered without a window, nothing is wide enough for a second column. */
    () => false,
  );
}
